// @flow

import I18n from '../../lib/i18n';
import {currentSubscriptionSelector} from '../Dashboard';
import type {StateType} from '../reducers';
import type {VoucherStateType} from './reducer';
import WizzeeLog from '../../lib/wizzeeLog';

export const voucherStateSelector = (state: StateType): VoucherStateType =>
  state.voucher;

export const voucherAvailableVouchersSelector = (state: StateType): Object[] =>
  voucherStateSelector(state).availabeVouchers;

export const voucherCurrentVoucherDetailsSelector = (
  state: StateType,
): Object => voucherStateSelector(state).currentVoucherDetails;

export const voucherCurrentVoucherExternalIdSelector = (
  state: StateType,
): Object => voucherCurrentVoucherDetailsSelector(state).externalId;

export const voucherCurrentVoucherLabelCodeSelector = (
  state: StateType,
): Object => voucherCurrentVoucherDetailsSelector(state).labelCode;

export const voucherComeFromSelector = (state: StateType): string =>
  voucherStateSelector(state).comeFrom;

export const voucherAvailableVouchersWithoutOrdersSelector = (
  state: StateType,
): Object[] =>
  voucherAvailableVouchersSelector(state).filter(
    v => !v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED'),
  );

// Available vouchers selectors filtered by order
export const voucherAvailableVouchersWithChangePlanOrderSelector = (
  state: StateType,
): Object[] =>
  voucherAvailableVouchersSelector(state).filter(
    v =>
      v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') &&
      v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('CHANGEPLAN'),
  );

export const voucherAvailableVouchersWithTopuUpOrderSelector = (
  state: StateType,
): Object[] =>
  voucherAvailableVouchersSelector(state).filter(
    v =>
      v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') &&
      v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('TOPUP'),
  );

export const voucherAvailableVouchersWithBoosterOrderSelector = (
  state: StateType,
): Object[] =>
  voucherAvailableVouchersSelector(state).filter(
    v =>
      v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') &&
      v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('BOOSTER'),
  );

export const voucherAvailableVouchersWithActivationOrderSelector = (
  state: StateType,
): Object[] =>
  voucherAvailableVouchersSelector(state).filter(
    v =>
      v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') &&
      v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('ACTIVATION'),
  );

export const voucherAvailableVouchersOnlyForCurrentSubscriptionSelector = (
  state: StateType,
): Object[] => {
  const currentSubscription = currentSubscriptionSelector(state);
  if (currentSubscription === null) return [];
  const subscriptionProductSerialNumber =
    currentSubscription.subscriptionProductSerialNumber;
  const allVouchers = voucherAvailableVouchersSelector(state);

  return allVouchers.filter(
    v =>
      v.characteristics.hasOwnProperty('SUBSCRIPTION_EXTERNAL_ID') &&
      v.characteristics.SUBSCRIPTION_EXTERNAL_ID.includes(
        subscriptionProductSerialNumber,
      ),
  );
};

export const voucherFilteredAvailableVouchersSelector = (
  state: StateType,
): Object[] => {
  const currentSubscription = currentSubscriptionSelector(state);
  if (currentSubscription === null) {
    return [];
  }

  const origin = voucherComeFromSelector(state);
  const availableVoucherList = voucherAvailableVouchersSelector(state);
  const filteredVoucherByOrigin = filteringVoucherByOrigin(
    origin,
    availableVoucherList,
  );
  const sortedVoucherByTimestamp = sortVoucherByTimestampAsc(
    filteredVoucherByOrigin,
  );
  const filteredVoucherBySubscriptionID =
    removingSubscriptionIDNotEqualCurrentSubscriptionId(
      sortedVoucherByTimestamp,
      currentSubscription,
    );
  const voucherList = removingUnmappedVouchers(filteredVoucherBySubscriptionID);

  return voucherList;
};

export const voucherFilteredAvailableVouchersSelectorLazy = (
  state: StateType,
): Object[] => {
  const currentSubscription = currentSubscriptionSelector(state);

  const origin = voucherComeFromSelector(state);
  const availableVoucherList = voucherAvailableVouchersSelector(state);

  let voucherList = filteringVoucherByOrigin(origin, availableVoucherList);
  voucherList = sortVoucherByTimestampAsc(voucherList);

  if (currentSubscription !== null) {
    voucherList = removingSubscriptionIDNotEqualCurrentSubscriptionId(
      voucherList,
      currentSubscription,
    );
  }

  voucherList = removingUnmappedVouchers(voucherList);

  return voucherList;
};

function removingUnmappedVouchers(voucherList: Object[]): Object[] {
  return voucherList.filter(item =>
    I18n.lookup(
      `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${item.labelCode}`,
    ),
  );
}

/**
 * Filters the vouchers list according to the screen/order origin
 * @param {*} origin the given origin which will filter over the vouchers's ORDER_CATEGORY_RESTRICTED characteristic
 * @param {*} voucherList the full voucher list with differents ORDER_CATEGORY_RESTRICTED
 * @returns
 */
function filteringVoucherByOrigin(
  origin: string,
  voucherList: Object[],
): Object[] {
  var filteredVoucherList = voucherList;

  switch (origin) {
    case 'menu':
    case 'dashboard':
      break;
    case 'signup':
    case 'subscription':
    case 'delivery':
    case 'chooseplan':
    case 'confirmplan':
      filteredVoucherList = voucherList.filter(
        v =>
          !v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('SIGNUP') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('DELIVERY') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('SUBSCRIPTION'),
      );
      break;
    case 'activation':
    case 'confirmplanduringactivate':
      filteredVoucherList = voucherList.filter(
        v =>
          !v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('ACTIVATION'),
      );
      break;
    case 'changeplan':
      filteredVoucherList = voucherList.filter(
        v =>
          !v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('CHANGEPLAN'),
      );
      break;
    case 'topup':
    case 'booster':
    case 'chooseinternationaloptions':
    case 'traveltopup':
      filteredVoucherList = voucherList.filter(
        v =>
          !v.characteristics.hasOwnProperty('ORDER_CATEGORY_RESTRICTED') ||
          v.characteristics.ORDER_CATEGORY_RESTRICTED.includes('TOPUP'),
      );
      break;
    default:
      WizzeeLog.log(
        `>>> filteringVoucherByOrigin() - origin ${origin} not known`,
      );
      break;
  }

  return filteredVoucherList;
}

function sortVoucherByTimestampAsc(voucherList: Object[]): Object[] {
  const sortFunc = (a, b) => {
    if (a.endDateTime && b.endDateTime) {
      return a.endDateTime - b.endDateTime;
    }
    return 1;
  };

  return voucherList.sort(sortFunc);
}

function removingSubscriptionIDNotEqualCurrentSubscriptionId(
  voucherList: Object[],
  currentSubscription: any,
): Object[] {
  return voucherList.filter(
    item =>
      !item.characteristics.hasOwnProperty('SUBSCRIPTION_EXTERNAL_ID') ||
      item.characteristics.SUBSCRIPTION_EXTERNAL_ID.includes(
        currentSubscription.subscriptionProductSerialNumber,
      ),
  );
}
